/**
 * Best-effort GPU model detection via the WEBGL_debug_renderer_info
 * extension. Browsers cannot read VRAM — this only gives a friendly renderer
 * name to pre-fill an editable input. Returns null if detection isn't
 * available (extension blocked, headless browser, unsupported device, etc.)
 * so callers can fall back to an empty, user-editable field.
 */
export function detectGpuModel(): string | null {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return null;

    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (!ext) return null;

    const raw = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL);
    if (!raw || typeof raw !== 'string') return null;

    return cleanRendererString(raw);
  } catch {
    return null;
  }
}

/**
 * Strips the "ANGLE (...)" wrapper Chrome/Edge report on Windows/Linux down
 * to a friendly GPU name, e.g.
 *   "ANGLE (NVIDIA, NVIDIA GeForce RTX 4090 Direct3D11 vs_5_0 ps_5_0, D3D11)"
 *   -> "NVIDIA GeForce RTX 4090"
 */
function cleanRendererString(raw: string): string {
  const angleMatch = raw.match(/ANGLE\s*\(([^)]+)\)/i);
  const inner = angleMatch ? angleMatch[1] : raw;
  const parts = inner.split(',').map((p) => p.trim());
  let friendly = parts.length >= 2 ? parts[1] : inner;
  friendly = friendly.replace(/\s*(direct3d11|direct3d9|opengl|vulkan|angle).*$/i, '').trim();
  friendly = friendly.replace(/\s+/g, ' ').trim();
  return friendly || raw.trim();
}
